// Секциите на началния екран на „Изписване": бързо начало (видът и причината
// за брак се попълват в листа) и изписаните днес в текущия склад. Всяка секция
// е глупав рендер — данните идват от страницата. Редовете на „Изписани днес"
// са плътни: ЕДИН ред „час · вид · причина · №, продукти, въвел · сума".
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Writeoff } from "../../../../../api/inventory";
import { formatMoney } from "../../../components/numbers";
import { SectionCard, SectionTitle } from "../../../components/list/ListBits";
import { ROW_DENSE } from "../../../components/list/density";
import { TruncatedText } from "../../../components/list/TruncatedText";
import { WriteoffKindBadge } from "../WriteoffKindBadge";
import { WRITEOFF_REASONS, type WriteoffPreset } from "../writeoffWorksheetShared";

export const QuickStartSection = ({
  disabled,
  onStart,
}: {
  disabled: boolean;
  onStart: (preset: WriteoffPreset) => void;
}) => {
  const { t } = useTranslation();
  const chips: { key: string; label: string; preset: WriteoffPreset }[] = [
    {
      key: "writeoff",
      label: t("inventoryPage.writeoff"),
      preset: { kind: "writeoff", reason: "" },
    },
    ...WRITEOFF_REASONS.map((reason) => ({
      key: reason.value,
      label: `${t("inventoryPage.scrap")} · ${t(`inventoryPage.${reason.key}`)}`,
      preset: { kind: "scrap" as const, reason: reason.value },
    })),
  ];
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle title={t("inventoryPage.woQuickTitle")} hint={t("inventoryPage.woQuickHint")} />
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            disabled={disabled}
            onClick={() => onStart(chip.preset)}
            className={`cursor-pointer rounded-full border px-3.5 py-2 text-[13px] leading-[normal] outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] disabled:cursor-not-allowed disabled:opacity-55 ${
              chip.preset.kind === "scrap"
                ? "border-danger/30 bg-surface text-ink hover:border-danger/60 hover:bg-danger-tint"
                : "border-shade-3 bg-surface text-ink hover:border-brand/45 hover:bg-brand-tint"
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>
    </section>
  );
};

// Изписаните днес (текущият склад), най-новите отгоре
export const TodayWriteoffsSection = ({
  writeoffs,
  onView,
}: {
  writeoffs: Writeoff[];
  onView: (writeoff: Writeoff) => void;
}) => {
  const { t } = useTranslation();
  const gross = writeoffs.reduce((sum, writeoff) => sum + (writeoff.totalValueWithVat || 0), 0);
  const hint = [
    t("inventoryPage.woDocsCount", { count: writeoffs.length }),
    writeoffs.length ? formatMoney(gross) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle title={t("inventoryPage.woTodayTitle")} hint={hint} />
      <SectionCard>
        {writeoffs.map((writeoff, index) => {
          const reason = writeoff.reason || writeoff.comment || t("inventoryPage.woNoReason");
          const meta = [
            `№${writeoff.id}`,
            t("inventoryPage.woProductsCount", { count: writeoff.productsCount || 0 }),
            writeoff.createdByName ?? "—",
          ].join(" · ");
          return (
            <button
              key={writeoff.id}
              type="button"
              onClick={() => onView(writeoff)}
              className={`flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-3.5 text-left text-ink outline-none hover:bg-fill focus-visible:bg-fill ${ROW_DENSE} ${
                index ? "border-t border-solid border-shade-1" : ""
              }`}
            >
              <span className="w-10 shrink-0 font-sheet text-[12.5px] text-body">
                {dayjs(writeoff.createdAt ?? writeoff.date).format("HH:mm")}
              </span>
              <WriteoffKindBadge kind={writeoff.kind} />
              <TruncatedText text={`${reason} · ${meta}`} className="flex-1">
                <span className="font-medium">{reason}</span>
                <span className="text-[12.5px] text-body"> · {meta}</span>
              </TruncatedText>
              <span className="shrink-0 font-sheet font-semibold">
                {formatMoney(writeoff.totalValueWithVat || 0)}
              </span>
            </button>
          );
        })}
        {writeoffs.length === 0 && (
          <div className="px-3.5 py-3 text-muted">{t("inventoryPage.woTodayNone")}</div>
        )}
      </SectionCard>
    </section>
  );
};
